import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { deepEqual } from 'fast-equals';

import OrderSummary from 'components/account/Order/OrderSummary';
import { fetchMyAccountContent } from 'apis/zcs';
import {
  removeItemForCancellationOrReturn,
  resetCancelOrReturnItems,
  restoreAccountState,
  selectItemForCancellationOrReturn,
  setItemsForCancellationOrReturn,
  setPreviousOrderAction
} from 'actions/account/account';
import { fetchOrderDetails, setIsOrderItemsUpdating, setIsShipmentOptionsLoading } from 'actions/account/order';
import { stepSelection, updateBoxNumInfo } from 'actions/account/returns';
import { resetShipmentTracking } from 'actions/account/shipmentTracking';
import { checkCustomerAuthentication } from 'actions/authentication';
import { fitSurveyReply, getCustomerFits, updateFitSurvey } from 'actions/landing/newsfeed';
import { firePixelServer } from 'actions/pixelServer';
import { sessionExpiration, setAndStoreCookie } from 'actions/session';
import { titaniteView, track } from 'apis/amethyst';
import BackNav from 'components/account/BackNav';
import AccountBreadcrumbs from 'components/account/AccountBreadcrumbs';
import CancelOrReturnItemSelect from 'components/account/Order/CancelOrReturnItemSelect';
import OrderInformation from 'components/account/Order/OrderInformation';
import OrderTableLayout from 'components/account/Order/OrderTableLayout';
import ReturnsAndExchanges from 'components/account/ReturnsAndExchanges';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { MY_ACCOUNT_DATE_FORMAT, STORE_ACCOUNT_LOCAL_STORAGE_KEY } from 'constants/appConstants';
import { EMAIL_TOKEN_COOKIE, EMAIL_TOKEN_SEARCH_PARAM } from 'constants/cookies';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import AccountHeader from 'containers/account/AccountHeader';
import { ORDER_ACTION_EXCHANGE, ORDER_ACTION_NONE, ORDER_ACTION_RETURN } from 'constants/orderActions';
import OrderCare from 'containers/account/OrderCare';
import { evCancelCheckedItemsClick, pvOrderDetail } from 'events/account';
import { evExchangeCheckedItemsClick, evSelectItemsToExchangeClick } from 'events/exchanges';
import { RETURN_INITIATE_SOURCE } from 'constants/amethystEnums';
import { formatDate, getIsOrderDateBeforeReturnStoreCreditStartDate, getIsWithinReturnWindow, parseDate } from 'helpers/dateUtils';
import { isMaybeGuestAuthOrderFlow } from 'helpers/MyAccountUtils';
import ReturnsUtils from 'helpers/ReturnsUtils';
import useMartyContext from 'hooks/useMartyContext';
import usePrevious from 'hooks/usePrevious';
import { loadFromLocalStorage, saveToLocalStorage } from 'helpers/localStorageUtilities';
import { evReturnInitiateClick } from 'events/returns';
import AmazonPayOrderCare from 'containers/account/AmazonPayOrderCare';
import { selectAuthentication } from 'selectors/authentication';
import withUseMyAccountRedesignHOC from 'helpers/useMyAccountRedesignHOCWrapper';
import { cn } from 'helpers/classnames';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/containers/account/order.scss';

export const Order = props => {
  const {
    account: { content, cancelOrReturnItems: items, previousOrderAction },
    killswitch: { keepExchangeOrdersFromReturnCancel, showAmazonPayDeclinedRemediationRestriction, showBatchReturn },
    location: { search },
    order: { order, paymentUpdated, isOrderItemsUpdating },
    newsfeed: { fits },
    shipmentTracking: { trackingInfo },
    isLoading,
    match: { params },
    resetCancelOrReturnItems,
    checkCustomerAuthentication,
    firePixelServer,
    fetchOrderDetails,
    getCustomerFits,
    resetShipmentTracking,
    history,
    setIsShipmentOptionsLoading,
    setIsOrderItemsUpdating,
    setPreviousOrderAction,
    restoreAccountState,
    fetchMyAccountContent,
    setItemsForCancellationOrReturn,
    isMyAccRedesignHookValue: isMyAccRedesign,
    isStoreCreditEnabled,
    forceStoreCreditStartDate
  } = props;
  const staleData = order && params.orderId !== order.orderId;
  const {
    testId,
    marketplace: {
      account: { multiBoxReturns },
      pdp: { showFitSurvey },
      returns: { handlesReturnShipping, storeCreditEnabledReturnWindowLength }
    },
    router
  } = useMartyContext();

  const returnMethodOfTheLatestReturnItem = ReturnsUtils.latestReturnTypes(order);
  const latestReturnMethodWithFallback = handlesReturnShipping ? returnMethodOfTheLatestReturnItem : PROVIDE_LABEL_OPTIONS.ANY_CARRIER;
  const [processedPreselected, setProcessedPreselected] = useState(false);
  const [isFitSurveyLoading, setIsFitSurveyLoading] = useState(false);
  const [sameOrderPaymentUpdated, setSameOrderPaymentUpdated] = useState(false);
  const [cancelItemsMobileVisible, setCancelItemsMobileVisible] = useState(false);
  const [returnItemsMobileVisible, setReturnItemsMobileVisible] = useState(false);
  const authentication = useSelector(selectAuthentication);

  const isUpdatingData = useRef(false);
  const prevOrderId = usePrevious(order?.orderId);
  const prevLineItems = usePrevious(order?.lineItems);

  const queryParams = queryString.parse(search);
  const { ohPage, ohPageSize, utm_campaign: utmCampaign, preselectedItems, didCancelReturn } = queryParams;
  const emailAuthToken = queryParams[EMAIL_TOKEN_SEARCH_PARAM];
  const isGuest = !!emailAuthToken; // presence of EMAIL_TOKEN_SEARCH_PARAM is an indicator of guest account

  // Uses util to check if return is within newly enforced window
  // that is different than marketplace window under certain circumstances
  const isWithinReturnWindow = order
    ? getIsWithinReturnWindow({
        orderDate: order?.orderDate,
        windowLength: storeCreditEnabledReturnWindowLength,
        forceStoreCreditStartDate: forceStoreCreditStartDate
      })
    : false;

  // Uses util that checks if the order is grandfathered in and doesnt need to use
  // new enforced return timeline
  const isGrandFatheredOutOfStoreCreditReturnWindow = order
    ? getIsOrderDateBeforeReturnStoreCreditStartDate({
        orderDate: order?.orderDate,
        windowLength: storeCreditEnabledReturnWindowLength,
        forceStoreCreditStartDate: forceStoreCreditStartDate
      })
    : true;

  const resetTableLayoutChanges = useCallback(() => {
    if (isUpdatingData.current) {
      if (authentication && !fits.length && !!showFitSurvey) {
        getCustomerFits();
      }

      if (items?.length) {
        previousOrderAction === ORDER_ACTION_NONE && resetCancelOrReturnItems();
      }

      if (trackingInfo?.length) {
        resetShipmentTracking();
      }

      isUpdatingData.current = false;
    }
  }, [
    authentication,
    fits,
    getCustomerFits,
    isUpdatingData,
    items,
    resetCancelOrReturnItems,
    resetShipmentTracking,
    showFitSurvey,
    trackingInfo,
    previousOrderAction
  ]);

  const fetchData = useCallback(
    (forceRefresh = false) => {
      // Check if cached order is different from requested order
      const staleData = (order && params.orderId !== order.orderId) || didCancelReturn;
      // Fetch only if the order data is not loaded or stale or forceRefresh flag is true
      if (!order || staleData || forceRefresh) {
        resetTableLayoutChanges();
        Promise.resolve(fetchOrderDetails(params.orderId, true)).then(response => {
          if (response) {
            track(() => [pvOrderDetail, { order: response, emailCampaign: utmCampaign }]);
          }

          // clear didCancelReturn query param to prevent infinite data fetching
          if (didCancelReturn) {
            history.replace({ search: '' });
          }
        });
      }
    },
    [resetTableLayoutChanges, order, params.orderId, fetchOrderDetails, utmCampaign]
  );

  useEffect(() => {
    if (isMyAccRedesign && order && !isMaybeGuestAuthOrderFlow(search)) {
      router.pushPreserveAppRoot(`/my-orders/${order?.orderId}`);
    }
  }, [isMyAccRedesign, order]);

  useEffect(() => {
    if (preselectedItems && order) {
      const preselectedItemsArray = typeof preselectedItems === 'string' ? preselectedItems : [...preselectedItems];
      const { orderId, lineItems } = order || {};
      if (orderId && lineItems && !isGuest && (!deepEqual(lineItems, prevLineItems) || !processedPreselected)) {
        setProcessedPreselected(true);
        const itemObjectsToPreselect = lineItems.filter(lineItem => preselectedItemsArray.includes(lineItem.itemUniqueId));
        const orderIdAddedObjects = itemObjectsToPreselect.map(lineItem => ({
          ...lineItem,
          orderId
        })); // orderId not on order object and is needed for return process.
        setItemsForCancellationOrReturn(orderIdAddedObjects);
      }
    }
  }, [
    order,
    preselectedItems,
    prevLineItems,
    setProcessedPreselected,
    processedPreselected,
    resetCancelOrReturnItems,
    setItemsForCancellationOrReturn
  ]);

  useEffect(() => {
    titaniteView();
  }, []);

  // This useEffect is to check if there's any previous account state needs to be recovered
  // This happens shen we are coming back from Balder with previous order action being Exchange or Return
  useEffect(() => {
    const previousAccountState = loadFromLocalStorage(STORE_ACCOUNT_LOCAL_STORAGE_KEY);
    if (previousAccountState?.cancelOrReturnItems[0]?.orderId === params.orderId) {
      restoreAccountState(previousAccountState);
    }
    window.localStorage.removeItem(STORE_ACCOUNT_LOCAL_STORAGE_KEY);
  }, [params.orderId, restoreAccountState]);

  useEffect(() => {
    const returnTo = `/orders/${params.orderId}`;
    if (prevOrderId !== order?.orderId) {
      isUpdatingData.current = true;
    }
    checkCustomerAuthentication(returnTo, () => fetchData(), {
      isEmailTokenAuthorized: true,
      redirectOnClose: '/'
    });
  }, [params.orderId, fetchData, checkCustomerAuthentication, prevOrderId, order]);

  useEffect(() => {
    firePixelServer('order-info');

    if (!content) {
      fetchMyAccountContent();
    }
  }, [content, fetchMyAccountContent, firePixelServer]);

  useEffect(() => {
    if (prevOrderId === order?.orderId && paymentUpdated && !sameOrderPaymentUpdated) {
      // if same order and paymentUpdated
      const forceRefresh = true;
      setSameOrderPaymentUpdated(true);
      isUpdatingData.current = true;
      fetchData(forceRefresh);
    }
  }, [order, prevOrderId, paymentUpdated, sameOrderPaymentUpdated, fetchData]);

  useEffect(() => {
    window.addEventListener('click', amethystClickEventHandler);
    return () => window.removeEventListener('click', amethystClickEventHandler);
  }, []);

  const amethystClickEventHandler = e => {
    const amethystReturnBehaviorSurvey = e.target.closest('[data-amethyst-return-behavior-survey]')?.dataset?.amethystReturnBehaviorSurvey;

    if (amethystReturnBehaviorSurvey) {
      track(() => [evReturnInitiateClick, { returnInitiateSource: amethystReturnBehaviorSurvey }]);
    }
  };

  const handleToggleItemSelection = (lineItem, orderId, isExchange) => {
    const {
      account: { cancelOrReturnItems: items },
      selectItemForCancellationOrReturn,
      removeItemForCancellationOrReturn,
      resetCancelOrReturnItems
    } = props;

    if (items.find(({ itemUniqueId }) => itemUniqueId === lineItem.itemUniqueId)) {
      !isExchange && removeItemForCancellationOrReturn(lineItem);
    } else {
      lineItem.orderId = orderId;
      isExchange &&
        resetCancelOrReturnItems() &&
        track(() => [evSelectItemsToExchangeClick, { orderId, lineItem, selected: true, deselected: false }]);
      selectItemForCancellationOrReturn(lineItem);
    }
  };

  const handleToggleItemSelectionForMobile = (lineItem, orderId) => {
    const {
      account: { cancelOrReturnItems: items },
      selectItemForCancellationOrReturn,
      removeItemForCancellationOrReturn
    } = props;

    if (items.find(({ itemUniqueId }) => itemUniqueId === lineItem.itemUniqueId)) {
      removeItemForCancellationOrReturn(lineItem);
    } else {
      lineItem.orderId = orderId;
      selectItemForCancellationOrReturn(lineItem);
    }
  };

  const handleCancelItems = () => {
    const {
      account: { cancelOrReturnItems: items },
      match: {
        params: { orderId }
      }
    } = props;
    const lineItemIds = queryString.stringify({
      lineItemId: items.map(item => item.id)
    });
    track(() => [evCancelCheckedItemsClick, { orderId, items }]);
    history.pushPreserveAppRoot(`/orders/${orderId}/cancel?${lineItemIds}`);
  };

  const handleReturnItems = () => {
    const {
      account,
      account: { cancelOrReturnItems: items },
      returns: { boxInfo },
      updateBoxNumInfo,
      stepSelection
    } = props;
    setIsShipmentOptionsLoading(true);
    setPreviousOrderAction(ORDER_ACTION_RETURN);
    updateBoxNumInfo(1, boxInfo, items); // initialize 1 box default in store

    if (items.length === 1 || !multiBoxReturns) {
      stepSelection('SELECT_LABEL');
      history.pushPreserveAppRoot('/return');
    } else {
      stepSelection('SELECT_BOX');
      history.pushPreserveAppRoot('/returns');
    }
    // Saving current account state to localStorage when leaving the page and going to Balder return page
    saveToLocalStorage(STORE_ACCOUNT_LOCAL_STORAGE_KEY, {
      ...account,
      previousOrderAction: ORDER_ACTION_RETURN
    });

    track(() => [
      evReturnInitiateClick,
      {
        returnInitiateSource: RETURN_INITIATE_SOURCE.ACCOUNT_PAGE_RETURN_ITEMS_BUTTON
      }
    ]);
  };

  const handleExchangeItems = () => {
    const {
      account,
      order: { order }
    } = props;
    const { cancelOrReturnItems: items } = account;
    setIsShipmentOptionsLoading(true);
    setPreviousOrderAction(ORDER_ACTION_EXCHANGE);
    track(() => [evExchangeCheckedItemsClick, {}]);
    // Saving current account state to localStorage when leaving the page and going to Balder exchange page
    saveToLocalStorage(STORE_ACCOUNT_LOCAL_STORAGE_KEY, {
      ...account,
      previousOrderAction: ORDER_ACTION_EXCHANGE
    });
    history.pushPreserveAppRoot(`/exchange?id=${order.orderId}&asin=${items[0].product.asin}`);
  };

  const handleSelectFitQuestion = ({ currentTarget }) => {
    const {
      fitSurveyReply,
      updateFitSurvey,
      newsfeed: { fits }
    } = props;
    const {
      value,
      parentNode: {
        dataset: { stockid: stockId, asin }
      },
      dataset: { selected }
    } = currentTarget;

    const fitSelected = fits.find(item => item.asin === asin);
    setIsFitSurveyLoading(true);
    // the customer has already selected a fit survey for this item
    if (fitSelected) {
      if (selected === 'true') {
        // if you're clicking an already selected fit, then remove it
        updateFitSurvey({ stockId, id: fitSelected.id, deleteFit: true }, value).then(() => {
          setIsFitSurveyLoading(false);
        });
      } else {
        // otherwise, select the new fit
        updateFitSurvey({ stockId, id: fitSelected.id }, value).then(() => {
          setIsFitSurveyLoading(false);
        });
      }
      // the customer has never selected a fit for this asin before
    } else {
      fitSurveyReply({ stockId }, value).then(() => {
        setIsFitSurveyLoading(false);
      });
    }
  };

  const hideCancelItemsMobile = () => {
    setCancelItemsMobileVisible(false);
  };

  const hideReturnItemsMobile = () => {
    setReturnItemsMobileVisible(false);
  };

  const makeOrderCareOrDetails = () => {
    const {
      order: {
        order,
        order: { amazonPayOrder, amazonPayRemediationURL }
      }
    } = props;
    const { customerActionRequired, paymentMethod, shippingAddress } = order;

    return customerActionRequired ? (
      amazonPayOrder && showAmazonPayDeclinedRemediationRestriction ? (
        <AmazonPayOrderCare amazonPayRemediationUrl={amazonPayRemediationURL} />
      ) : (
        <OrderCare />
      )
    ) : (
      <OrderInformation order={order} payment={paymentMethod} address={shippingAddress} paymentUpdated={sameOrderPaymentUpdated} />
    );
  };

  const updateOrderItems = () => {
    setIsOrderItemsUpdating(true);
    Promise.resolve(fetchOrderDetails(params.orderId, true)).then(response => {
      if (response) {
        track(() => [pvOrderDetail, { order: response, emailCampaign: utmCampaign }]);
      }
      setIsOrderItemsUpdating(false);
    });
  };

  if (isLoading || !order || staleData) {
    return <PageLoader centered={true} />;
  } else if (cancelItemsMobileVisible) {
    return (
      <CancelOrReturnItemSelect
        type="cancel"
        order={order}
        selectedItems={items}
        onHideItemSelect={hideCancelItemsMobile}
        onToggleItemSelect={handleToggleItemSelectionForMobile}
        onSubmit={handleCancelItems}
      />
    );
  } else if (returnItemsMobileVisible) {
    return (
      <CancelOrReturnItemSelect
        type="return"
        order={order}
        selectedItems={items}
        onHideItemSelect={hideReturnItemsMobile}
        onToggleItemSelect={handleToggleItemSelectionForMobile}
        onSubmit={handleReturnItems}
      />
    );
  } else {
    const { orderId, orderDate } = order;
    const ordersUrl = `/orders?${queryString.stringify({
      page: ohPage,
      pageSize: ohPageSize
    })}`;
    const breadcrumbItems = [{ text: 'Order History', to: ordersUrl }];
    return (
      <SiteAwareMetadata className={css.orderDetailHeader}>
        <div className={css.orderDetail}>
          <AccountBreadcrumbs links={breadcrumbItems} currentText={'Order Information'} />
          <div className={css.accountHeader}>
            <AccountHeader title="Order Information" />
          </div>
          {authentication && (
            <div className={css.backNav}>
              <BackNav to="/orders">Order History</BackNav>
            </div>
          )}
          <div className={css.orderInfo}>
            {' '}
            {/* @todo consolidate this https://github.zappos.amazon.dev/mweb/marty/issues/5952 */}
            {sameOrderPaymentUpdated && (
              <p className={css.paymentUpdateSuccess}>
                <strong>Success!</strong> Your card has been updated. We're processing your order now.
              </p>
            )}
            <div>
              <span className={cn(css.orderDate, 'zappos:body-xl-medium zappos:text-[28px]')} data-test-id={testId('orderDate')}>
                {formatDate('MMM D YYYY', parseDate(orderDate, MY_ACCOUNT_DATE_FORMAT))}
              </span>
            </div>
            <div>
              <span className={cn(css.orderId, 'zappos:body-s')} data-test-id={testId('orderId')}>
                #{orderId}
              </span>
            </div>
          </div>
          {makeOrderCareOrDetails()}
          <OrderTableLayout
            order={order}
            keepExchangeOrdersFromReturnCancel={!!keepExchangeOrdersFromReturnCancel}
            isFitSurveyLoading={isFitSurveyLoading}
            isOrderItemsUpdating={isOrderItemsUpdating}
            fitItems={fits}
            onSelectFitQuestion={handleSelectFitQuestion}
            onToggleItemSelect={handleToggleItemSelection}
            selectedItems={items}
            resetSelectedItems={resetCancelOrReturnItems}
            updateOrderItems={updateOrderItems}
            handleReturnItems={handleReturnItems}
            handleExchangeItems={handleExchangeItems}
            handleCancelItems={handleCancelItems}
            isStoreCreditEnabled={isStoreCreditEnabled}
            isGuest={isGuest}
            isWithinReturnWindow={isWithinReturnWindow}
            isGrandFatheredOutOfStoreCreditReturnWindow={isGrandFatheredOutOfStoreCreditReturnWindow}
          />

          <OrderSummary order={order} keepExchangeOrdersFromReturnCancel={!!keepExchangeOrdersFromReturnCancel} showBatchReturn={showBatchReturn} />
          {returnMethodOfTheLatestReturnItem && <ReturnsAndExchanges labelDelivery={latestReturnMethodWithFallback} />}
        </div>
      </SiteAwareMetadata>
    );
  }
};

// Guest checkout customers receive a confirmation email that appends `auth_token=foo` to the order confirmation page URL.
// Check for `auth_token` and move it to cookies to support use in supported actions.
const setParamEmailTokenCookie = (store, location) => {
  const searchParams = new URLSearchParams(location.search);
  const emailToken = searchParams.get(EMAIL_TOKEN_SEARCH_PARAM);

  if (emailToken) {
    store.dispatch(setAndStoreCookie(EMAIL_TOKEN_COOKIE, emailToken, sessionExpiration()));
  }
};

Order.beforeFetchDataOnServer = (store, location) => {
  setParamEmailTokenCookie(store, location);
};

const mapStateToProps = state => ({
  account: state.account,
  killswitch: state.killswitch,
  order: state.order,
  returns: state.returns,
  newsfeed: state.newsfeed,
  shipmentTracking: state.shipmentTracking
});

export default connect(mapStateToProps, {
  fetchMyAccountContent,
  checkCustomerAuthentication,
  fetchOrderDetails,
  firePixelServer,
  fitSurveyReply,
  getCustomerFits,
  removeItemForCancellationOrReturn,
  resetCancelOrReturnItems,
  resetShipmentTracking,
  selectItemForCancellationOrReturn,
  setItemsForCancellationOrReturn,
  stepSelection,
  updateBoxNumInfo,
  updateFitSurvey,
  setIsShipmentOptionsLoading,
  setIsOrderItemsUpdating,
  setPreviousOrderAction,
  restoreAccountState
})(withUseMyAccountRedesignHOC(Order, 'isMyAccRedesignHookValue'));
